import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Badge,
  Card,
  List,
  ListItem,
  makeStyles,
  mergeClasses,
  tokens,
  Tooltip,
} from '@fluentui/react-components';
import {
  MeetingReport,
  MeetingType,
  Priority,
  QuestionStatus,
  RiskSeverity,
} from '@axys-notes/common';
import React, { forwardRef, RefObject, useRef, useState } from 'react';
import { MeetingNotesActionsBar } from './meeting-notes-actions-bar';
import { RegenerateReportDialog } from '../regenerate-report-dialog';
import { AIGeneratedDisclaimer } from '../ai-generated-disclaimer';
import {
  PeopleTeamRegular,
  PersonRegular,
  LightbulbRegular,
  BuildingRegular,
  TaskListSquareLtrRegular,
  ChatRegular,
  QuestionCircleRegular,
  WarningRegular,
} from '@fluentui/react-icons';

type Props = {
  className?: string;
  recordingId: string;
  globalInfosRef: RefObject<HTMLDivElement>;
  report: MeetingReport;
  editable: boolean;
  userContext?: string;
  transcriptExpired?: boolean;
};

const useStyles = makeStyles({
  cardStyle: {
    backgroundColor: tokens.colorSubtleBackgroundHover,
    gap: 0,
  },
  actionsBar: {
    display: 'flex',
    marginBottom: tokens.spacingVerticalL,
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    paddingLeft: '15px',
    marginBottom: tokens.spacingVerticalL,
  },
  h2Style: { fontWeight: 'bold', margin: 0 },
  h5Style: { margin: '0px', fontSize: '14px' },
  firstLevelListItemStyle: {
    listStyleType: 'circle',
    marginLeft: '30px',
    marginBottom: '5px',
  },
  secondLevelListItemStyle: {
    listStyleType: 'square',
    marginLeft: '20px',
    marginBottom: '5px',
  },
  elementTitleStyle: {
    fontWeight: 'bold',
  },
  accordionPanelStyle: { margin: '0 30px' },
  accordionItemStyle: { margin: '20px 0px' },
  priorityBadge: {
    marginLeft: '8px',
    fontSize: '11px',
  },
  meetingTypeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  metricsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    padding: '10px 0',
  },
  metricItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '120px',
    padding: '8px 12px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '8px',
  },
  metricValue: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: tokens.colorBrandForeground1,
  },
  metricLabel: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  statusBadge: {
    marginLeft: '8px',
  },
});

// Labels pour les types de réunion
const MEETING_TYPE_LABELS: Record<MeetingType, { label: string; icon: React.ReactNode }> = {
  team_standup: { label: "Réunion d'équipe", icon: <PeopleTeamRegular /> },
  client: { label: 'Réunion client', icon: <BuildingRegular /> },
  brainstorm: { label: 'Brainstorming', icon: <LightbulbRegular /> },
  one_on_one: { label: 'Entretien individuel', icon: <PersonRegular /> },
  project_review: { label: 'Revue de projet', icon: <TaskListSquareLtrRegular /> },
  decision: { label: 'Réunion de décision', icon: <ChatRegular /> },
  general: { label: 'Réunion générale', icon: <ChatRegular /> },
};

// Couleurs pour les priorités
const PRIORITY_COLORS: Record<Priority, 'danger' | 'warning' | 'success'> = {
  high: 'danger',
  medium: 'warning',
  low: 'success',
};

const PRIORITY_LABELS: Record<Priority, string> = {
  high: 'Haute',
  medium: 'Moyenne',
  low: 'Basse',
};

// Couleurs pour les sévérités de risque
const SEVERITY_COLORS: Record<RiskSeverity, 'danger' | 'warning' | 'success'> = {
  high: 'danger',
  medium: 'warning',
  low: 'success',
};

const SEVERITY_LABELS: Record<RiskSeverity, string> = {
  high: 'Élevée',
  medium: 'Modérée',
  low: 'Faible',
};

// Labels pour les statuts de question
const STATUS_COLORS: Record<QuestionStatus, 'informative' | 'success' | 'warning'> = {
  open: 'informative',
  answered: 'success',
  deferred: 'warning',
};

const STATUS_LABELS: Record<QuestionStatus, string> = {
  open: 'Ouverte',
  answered: 'Répondue',
  deferred: 'Reportée',
};

// Composant pour afficher un badge de priorité
const PriorityBadge = ({ priority, className }: { priority?: Priority; className?: string }) => {
  if (!priority) return null;
  return (
    <Badge
      appearance="filled"
      color={PRIORITY_COLORS[priority]}
      className={className}
      size="small"
    >
      {PRIORITY_LABELS[priority]}
    </Badge>
  );
};

// Composant pour afficher le type de réunion
const MeetingTypeBadge = ({ type, className }: { type?: MeetingType; className?: string }) => {
  if (!type) return null;
  const config = MEETING_TYPE_LABELS[type];
  return (
    <Badge appearance="outline" color="brand" className={className} size="medium">
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {config.icon}
        {config.label}
      </span>
    </Badge>
  );
};

// Formater la durée en minutes:secondes
const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) {
    return `${mins}m ${secs}s`;
  }
  return `${secs}s`;
};

export const MeetingNotes = forwardRef<HTMLDivElement, Props>(
  (props: Props, ref) => {
    const {
      recordingId,
      report,
      globalInfosRef,
      className,
      editable,
      userContext,
      transcriptExpired,
    } = props;
    const meetingNotesRef = useRef<HTMLDivElement>(null);
    const styles = useStyles();
    const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);

    const hasQuestions = report.questions && report.questions.length > 0;
    const hasRisks = report.risks && report.risks.length > 0;
    const hasMetrics = report.participantMetrics && report.participantMetrics.participants.length > 0;

    // Déterminer les sections ouvertes par défaut
    const defaultOpenItems = [
      'summary',
      'topics',
      'actions',
      'decisions',
      'deadlines',
      ...(hasQuestions ? ['questions'] : []),
      ...(hasRisks ? ['risks'] : []),
    ];

    return (
      <>
        <Card
          ref={ref}
          className={
            className
              ? mergeClasses(styles.cardStyle, className)
              : styles.cardStyle
          }
        >
        <div className={styles.actionsBar}>
          <MeetingNotesActionsBar
            recordingId={recordingId}
            meetingNotesref={meetingNotesRef}
            globalInfosRef={globalInfosRef}
            editable={editable}
            transcriptExpired={transcriptExpired}
            onRegenerateClick={() => setRegenerateDialogOpen(true)}
          />
        </div>
        <div ref={meetingNotesRef}>
          <div className={styles.headerContainer}>
            <h2 className={styles.h2Style}>Compte-Rendu de la réunion</h2>
            <MeetingTypeBadge type={report.meetingType} className={styles.meetingTypeBadge} />
          </div>

          <AIGeneratedDisclaimer />

          {/* Métriques de participation */}
          {hasMetrics && (
            <div style={{ padding: '0 15px', marginBottom: '15px' }}>
              <div className={styles.metricsContainer}>
                <Tooltip content="Durée totale de la réunion" relationship="label">
                  <div className={styles.metricItem}>
                    <span className={styles.metricValue}>
                      {Math.round(report.participantMetrics!.totalDuration / 60)} min
                    </span>
                    <span className={styles.metricLabel}>Durée</span>
                  </div>
                </Tooltip>
                <Tooltip content="Nombre de participants" relationship="label">
                  <div className={styles.metricItem}>
                    <span className={styles.metricValue}>
                      {report.participantMetrics!.participants.length}
                    </span>
                    <span className={styles.metricLabel}>Participants</span>
                  </div>
                </Tooltip>
                {report.participantMetrics!.participants.slice(0, 3).map((p, i) => (
                  <Tooltip
                    key={i}
                    content={`${p.interventionCount} interventions, durée moyenne: ${formatDuration(p.averageInterventionLength)}`}
                    relationship="label"
                  >
                    <div className={styles.metricItem}>
                      <span className={styles.metricValue}>{p.speakingPercentage}%</span>
                      <span className={styles.metricLabel}>{p.name}</span>
                    </div>
                  </Tooltip>
                ))}
              </div>
            </div>
          )}

          <div style={{ padding: '0 10px' }}>
            <Accordion
              collapsible
              multiple
              defaultOpenItems={defaultOpenItems}
            >
              {/* Synthèse */}
              <AccordionItem
                value="summary"
                className={styles.accordionItemStyle}
              >
                <AccordionHeader
                  style={{ color: tokens.colorBrandForeground1 }}
                >
                  <h3>Synthèse</h3>
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanelStyle}>
                  {report.summary}
                </AccordionPanel>
              </AccordionItem>

              {/* Sujets abordés */}
              <AccordionItem
                value="topics"
                className={styles.accordionItemStyle}
              >
                <AccordionHeader
                  style={{ color: tokens.colorBrandForeground1 }}
                >
                  <h3>Sujets abordés ({report.topics.length})</h3>
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanelStyle}>
                  <Accordion
                    collapsible
                    multiple
                    defaultOpenItems={[...report.topics.keys()].map(
                      (i) => `topic-${i}`
                    )}
                  >
                    {report.topics.map((topic, indexTopic) => (
                      <AccordionItem
                        key={indexTopic}
                        value={`topic-${indexTopic}`}
                        style={{ marginBottom: 20 }}
                      >
                        <AccordionHeader
                          style={{ color: tokens.colorBrandForeground1 }}
                        >
                          {topic.title}
                        </AccordionHeader>
                        <AccordionPanel className={styles.accordionPanelStyle}>
                          <div>
                            <p>{topic.context}</p>
                            <Accordion
                              collapsible
                              multiple
                              defaultOpenItems={[
                                `topic-${indexTopic}-keyPoints`,
                              ]}
                            >
                              {topic.keyPoints.length > 0 && (
                                <List style={{ marginTop: 5 }}>
                                  {topic.keyPoints.map(
                                    (keyPoint, indexKeypoint) => (
                                      <ListItem
                                        key={`${indexTopic}-keypoint-${indexKeypoint}`}
                                        className={
                                          styles.firstLevelListItemStyle
                                        }
                                      >
                                        <div>
                                          <span
                                            className={styles.elementTitleStyle}
                                          >
                                            {keyPoint.title} :{' '}
                                          </span>
                                          {keyPoint.context}
                                        </div>
                                      </ListItem>
                                    )
                                  )}
                                </List>
                              )}
                            </Accordion>
                          </div>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </AccordionPanel>
              </AccordionItem>

              {/* Actions */}
              <AccordionItem
                value="actions"
                className={styles.accordionItemStyle}
              >
                <AccordionHeader
                  style={{ color: tokens.colorBrandForeground1 }}
                >
                  <h3>Actions ({report.actions.length})</h3>
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanelStyle}>
                  <List>
                    {report.actions.map((action, actionIndex) => (
                      <ListItem
                        key={`action-${actionIndex}`}
                        className={styles.firstLevelListItemStyle}
                      >
                        <span className={styles.elementTitleStyle}>
                          {action.title}
                          <PriorityBadge priority={action.priority} className={styles.priorityBadge} />
                          {' : '}
                        </span>
                        {action.context}
                        {!!action.owners?.length && (
                          <List>
                            {action.owners.map((owner, ownerIndex) => (
                              <ListItem
                                key={`action-${actionIndex}-owner-${ownerIndex}`}
                                className={styles.secondLevelListItemStyle}
                              >
                                {owner}
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </ListItem>
                    ))}
                  </List>
                </AccordionPanel>
              </AccordionItem>

              {/* Décisions */}
              <AccordionItem
                value="decisions"
                className={styles.accordionItemStyle}
              >
                <AccordionHeader
                  style={{ color: tokens.colorBrandForeground1 }}
                >
                  <h3>Décisions ({report.decisions.length})</h3>
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanelStyle}>
                  <List>
                    {report.decisions.map((decision, decisionIndex) => {
                      return (
                        <ListItem
                          key={`decision-${decisionIndex}`}
                          className={styles.firstLevelListItemStyle}
                        >
                          <span className={styles.elementTitleStyle}>
                            {decision.title}
                            <PriorityBadge priority={decision.priority} className={styles.priorityBadge} />
                            {' : '}
                          </span>
                          {decision.context}
                          {!!decision.owners?.length && (
                            <List>
                              {decision.owners.map((owner, ownerIndex) => (
                                <ListItem
                                  key={`decision-${decisionIndex}-owner-${ownerIndex}`}
                                  className={styles.secondLevelListItemStyle}
                                >
                                  {owner}
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionPanel>
              </AccordionItem>

              {/* Échéances */}
              <AccordionItem
                value="deadlines"
                className={styles.accordionItemStyle}
              >
                <AccordionHeader
                  style={{ color: tokens.colorBrandForeground1 }}
                >
                  <h3>Échéances ({report.deadlines.length})</h3>
                </AccordionHeader>
                <AccordionPanel className={styles.accordionPanelStyle}>
                  <List>
                    {report.deadlines.map((deadline, deadlineIndex) => {
                      return (
                        <ListItem
                          key={`deadline-${deadlineIndex}`}
                          className={styles.firstLevelListItemStyle}
                        >
                          <span className={styles.elementTitleStyle}>
                            {deadline.title}{' '}
                            {deadline.date ? `(${deadline.date})` : ''}
                            <PriorityBadge priority={deadline.priority} className={styles.priorityBadge} />
                            {' : '}
                          </span>
                          {deadline.context}
                          {!!deadline.owners?.length && (
                            <List>
                              {deadline.owners.map((owner, ownerIndex) => (
                                <ListItem
                                  key={`deadline-${deadlineIndex}-owner-${ownerIndex}`}
                                  className={styles.secondLevelListItemStyle}
                                >
                                  {owner}
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionPanel>
              </AccordionItem>

              {/* Questions (nouveau) */}
              {hasQuestions && (
                <AccordionItem
                  value="questions"
                  className={styles.accordionItemStyle}
                >
                  <AccordionHeader
                    style={{ color: tokens.colorBrandForeground1 }}
                  >
                    <h3>
                      <QuestionCircleRegular style={{ marginRight: '8px' }} />
                      Questions en suspens ({report.questions!.length})
                    </h3>
                  </AccordionHeader>
                  <AccordionPanel className={styles.accordionPanelStyle}>
                    <List>
                      {report.questions!.map((question, questionIndex) => (
                        <ListItem
                          key={`question-${questionIndex}`}
                          className={styles.firstLevelListItemStyle}
                        >
                          <span className={styles.elementTitleStyle}>
                            {question.question}
                            <Badge
                              appearance="filled"
                              color={STATUS_COLORS[question.status]}
                              className={styles.statusBadge}
                              size="small"
                            >
                              {STATUS_LABELS[question.status]}
                            </Badge>
                          </span>
                          <div style={{ marginTop: '4px' }}>{question.context}</div>
                          {question.askedBy && (
                            <div style={{ marginTop: '4px', fontStyle: 'italic', color: tokens.colorNeutralForeground3 }}>
                              Posée par : {question.askedBy}
                            </div>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </AccordionPanel>
                </AccordionItem>
              )}

              {/* Risques (nouveau) */}
              {hasRisks && (
                <AccordionItem
                  value="risks"
                  className={styles.accordionItemStyle}
                >
                  <AccordionHeader
                    style={{ color: tokens.colorBrandForeground1 }}
                  >
                    <h3>
                      <WarningRegular style={{ marginRight: '8px' }} />
                      Risques et blockers ({report.risks!.length})
                    </h3>
                  </AccordionHeader>
                  <AccordionPanel className={styles.accordionPanelStyle}>
                    <List>
                      {report.risks!.map((risk, riskIndex) => (
                        <ListItem
                          key={`risk-${riskIndex}`}
                          className={styles.firstLevelListItemStyle}
                        >
                          <span className={styles.elementTitleStyle}>
                            {risk.title}
                            <Badge
                              appearance="filled"
                              color={SEVERITY_COLORS[risk.severity]}
                              className={styles.statusBadge}
                              size="small"
                            >
                              {SEVERITY_LABELS[risk.severity]}
                            </Badge>
                          </span>
                          <div style={{ marginTop: '4px' }}>{risk.context}</div>
                          {risk.mitigation && (
                            <div style={{ marginTop: '4px', color: tokens.colorNeutralForeground2 }}>
                              <strong>Mitigation :</strong> {risk.mitigation}
                            </div>
                          )}
                          {!!risk.owners?.length && (
                            <List>
                              {risk.owners.map((owner, ownerIndex) => (
                                <ListItem
                                  key={`risk-${riskIndex}-owner-${ownerIndex}`}
                                  className={styles.secondLevelListItemStyle}
                                >
                                  {owner}
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </AccordionPanel>
                </AccordionItem>
              )}
            </Accordion>
          </div>
        </div>
      </Card>
      <RegenerateReportDialog
        recordingId={regenerateDialogOpen ? recordingId : null}
        currentContext={userContext}
        onClose={() => setRegenerateDialogOpen(false)}
      />
    </>
    );
  }
);
